import type { LucideIcon } from "lucide-react"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import type { ReactNode } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export interface MultiSelectOption {
  value: string
  label: string
  icon?: LucideIcon
}

interface AppMultiSelectFilterProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (values: string[]) => void
  /** Texto do gatilho quando nada está selecionado. */
  placeholder: string
  searchPlaceholder: string
  emptyMessage: string
  ariaLabel: string
  /** Slot acima da lista — usado pelo status para o alternador incluir/excluir. */
  header?: ReactNode
  /** Prefixo do gatilho quando há seleção (ex.: "exceto"). */
  selectionPrefix?: string
  className?: string
}

/**
 * Filtro de seleção múltipla com busca. Aplica no clique (sem botão "ok"): a
 * consulta só dispara quando o formulário é submetido.
 */
export const AppMultiSelectFilter = ({
  options,
  selected,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  ariaLabel,
  header,
  selectionPrefix,
  className,
}: AppMultiSelectFilterProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value]
    )

  const selectedLabels = options
    .filter((option) => selected.includes(option.value))
    .map((option) => option.label)

  const triggerLabel = selectedLabels.length
    ? [selectionPrefix, selectedLabels.join(", ")]
        .filter(Boolean)
        .join(" ")
    : placeholder

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          className={cn(
            "h-10 w-full justify-between gap-2 font-normal",
            className
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDownIcon
            size={16}
            className="shrink-0 opacity-50"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0"
        align="start"
      >
        {header && (
          <div className="border-b p-2">{header}</div>
        )}
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(
                  option.value
                )
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggle(option.value)}
                  >
                    <span
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded border",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input"
                      )}
                    >
                      {isSelected && (
                        <CheckIcon size={12} />
                      )}
                    </span>
                    {option.icon && (
                      <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected.length > 0 && (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => onChange([])}
            >
              {t("common.clearSelection")}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default AppMultiSelectFilter
